import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { extractApiErrorMessage } from "@velizhanin-dev/api-errors"
import type { TraceEffect } from "@velizhanin-dev/bot-scenario"
import { useMemo, useState } from "react"
import { toast } from "sonner"
import {
	botBuilderKeys,
	getTestChat,
	resetTestChat,
	sendTestEvent,
	type TestChatState,
	type TestEvent,
} from "@/entities/bot-builder"

/** Трейсы про блок, которого уже нет или который не сработал, их не подсвечиваем. */
const SKIPPED_TRACES: ReadonlySet<TraceEffect["code"]> = new Set([
	"button_stale",
	"wakeup_stale",
	"node_missing",
	"goto_target_missing",
])

/** Блоки, которых коснулся движок за последнее событие, их подсвечивает холст. */
export function tracedNodeIds(traces: TraceEffect[] | undefined): string[] {
	if (!traces) return []
	const ids = traces.flatMap((trace) =>
		trace.nodeId && !SKIPPED_TRACES.has(trace.code) ? [trace.nodeId] : [],
	)
	return [...new Set(ids)]
}

/**
 * Тест-чат по черновику. События уходят по одному: пока бот отвечает, новое
 * сообщение не отправляется, иначе ответы перемешаются.
 */
export function useTestChat(
	scenarioId: string,
	enabled: boolean,
	/** Тест идёт по черновику на сервере: сначала должны сохраниться правки. */
	beforeSend: () => Promise<void> = () => Promise.resolve(),
) {
	const queryClient = useQueryClient()
	const key = botBuilderKeys.testChat(scenarioId)
	const [lastTraces, setLastTraces] = useState<TraceEffect[]>([])

	const query = useQuery({
		queryKey: key,
		queryFn: () => getTestChat(scenarioId),
		enabled,
		staleTime: 0,
	})

	const activeNodeIds = useMemo(() => new Set(tracedNodeIds(lastTraces)), [lastTraces])

	const apply = (state: TestChatState) => {
		queryClient.setQueryData(key, state)
		setLastTraces(state.traces ?? [])
	}

	const send = useMutation({
		mutationFn: async (event: TestEvent) => {
			await beforeSend()
			return sendTestEvent(scenarioId, event)
		},
		onSuccess: apply,
		onError: (error) => toast.error(extractApiErrorMessage(error, "Бот не ответил")),
	})

	const reset = useMutation({
		mutationFn: () => resetTestChat(scenarioId),
		onSuccess: apply,
		onError: (error) => toast.error(extractApiErrorMessage(error, "Не удалось сбросить тест-чат")),
	})

	return {
		state: query.data,
		isLoading: query.isLoading,
		isBusy: send.isPending || reset.isPending,
		/** Промис не отклоняется: ошибка уже показана тостом. */
		send: (event: TestEvent) =>
			send.isPending
				? Promise.resolve()
				: send.mutateAsync(event).then(
						() => undefined,
						() => undefined,
					),
		reset: () =>
			reset.mutateAsync().then(
				() => undefined,
				() => undefined,
			),
		lastTraces,
		activeNodeIds,
	}
}

export type TestChat = ReturnType<typeof useTestChat>
